import type { SxProps, Theme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { DateTime, Info } from "luxon";
import type { CSSProperties } from "react";
import { type ExactShiftFilterType, isSelectedTimeFrameValidForShiftFilter, type ShiftFilterType } from "../../../components/staffing/MonthlyView/utils";
import type { Department } from "../../../contexts/AuthProvider";
import { INK, INK_BLACK, NEUTRAL, STATUS, SURFACE } from "../../../theme/tokens";
import { configuredAmount } from "../../../utils/censusResolution";
import {
	type DepartmentLocation,
	type DepartmentType,
	type Nurse,
	NurseFullRole,
	NurseFullRoleShort,
	NurseRole,
	type RecurringShift,
	type RecurringShiftRecurrenceType,
	type ShiftType,
} from "../../../utils/models";
import type { MultiSortOption } from "../../../utils/multiColumnSorting";
import { multiSortComparator, type SortingState } from "../../../utils/multiColumnSorting";
import { getShiftIndexByShiftTimes, nursePreferencesToDaysOffAndPto } from "../../../utils/utilFunctions";
import { MASTER_SCHEDULE_ROOT_ID } from "./consts";
import type { WeeksAndActiveState } from "./useGetMasterScheduleWeeksAndActiveState";

/** Returns the lowercase 3-letter day abbreviation for a date (e.g. "sun", "mon"). */
export const getDayKey = (dayDate: DateTime): string => dayDate.setLocale("en").toFormat("ccc").toLowerCase();

/** Returns the per-day amount for a location, falling back to the scalar max (`configuredAmount`). */
export const getDayExpectedAmount = (location: DepartmentLocation, dayDate: DateTime): number => configuredAmount(location, getDayKey(dayDate));

/**
 * Builds a composite key for a DepartmentLocation that identifies
 * its "logical location" — same name, shift window, roles/jobs, and section.
 * Used to detect sibling location entries that differ only by day/amount.
 */
export const getLocationKey = (loc: DepartmentLocation, useJobs = false): string => {
	const groupingStr = useJobs && loc.jobId ? loc.jobId : [...loc.roles].sort().join(",");
	return [loc.location, loc.shift_start.substring(0, 5), loc.shift_end.substring(0, 5), groupingStr, loc.section].join("|");
};

/**
 * Gets the index of a day given its name.
 * @param dayName - Name of the day (e.g., "Monday").
 * @returns Index of the day (0=Sunday, 1=Monday, ..., 6=Saturday) or null if not found.
 */
export function getDayIndex(dayName: string): number | null {
	const names = Info.weekdays("long", { locale: "en-US" }).map((s) => s.toLowerCase());
	const idx = names.indexOf(dayName.toLowerCase());
	return idx === -1 ? null : (idx + 1) % 7; // 0=Sunday … 6=Saturday
}

/**
 * Shared "does this department location fit this recurring shift?" predicates, used by both the
 * weekly (`buildScheduleData`) and consecutive (`buildConsecutiveScheduleData`) placement logic.
 * Kept granular (name / times / role-or-job) because the weekly builder needs to combine them
 * differently per step (Synergy matches by name only; the weekly `find` also checks weekdays).
 */
/** Location name match — `""` denotes the "no location" bucket (recurring shift with no location). */
export const locationMatchesShiftName = (location: Pick<DepartmentLocation, "location">, recurringShift: Pick<RecurringShift, "location">): boolean =>
	location.location === (recurringShift.location ?? "");

/** Clock-time match, normalized to HH:mm. */
export const locationMatchesShiftTimes = (
	location: Pick<DepartmentLocation, "shift_start" | "shift_end">,
	recurringShift: Pick<RecurringShift, "shiftStart" | "shiftEnd">,
): boolean =>
	location.shift_start.substring(0, 5) === recurringShift.shiftStart.substring(0, 5) &&
	location.shift_end.substring(0, 5) === recurringShift.shiftEnd.substring(0, 5);

/** Role/job match (non-Synergy): job id when job management is on and both sides carry one, else nurse role. */
export const locationMatchesShiftRoleOrJob = (
	location: Pick<DepartmentLocation, "roles" | "jobId">,
	recurringShift: Pick<RecurringShift, "jobId">,
	relevantRole: NurseRole,
	jobManagementEnabled: boolean,
): boolean =>
	jobManagementEnabled && recurringShift.jobId && location.jobId
		? recurringShift.jobId === location.jobId
		: location.roles.includes("all") || location.roles.includes(NurseFullRole[relevantRole]);

/** Weekday match (weekly, non-Synergy): the location runs on every weekday the shift repeats on, compared by 3-letter prefix. */
export const locationMatchesShiftWeekdays = (location: Pick<DepartmentLocation, "days">, weekdays: readonly string[]): boolean =>
	weekdays.every((requiredDay) => location.days.map((locDay) => locDay.toLowerCase().substring(0, 3)).includes(requiredDay.toLowerCase().substring(0, 3)));

export const blockedCellSx = {
	backgroundColor: NEUTRAL[300],
	height: "100%",
	cursor: "not-allowed",
} as const satisfies SxProps<Theme>;

// Theme-aware sx for the page heading. Use as `sx={headingSx}`.
export const headingSx: SxProps<Theme> = (theme: Theme) => ({
	fontWeight: 600,
	fontStyle: "normal",
	fontSize: "26px",
	lineHeight: "100%",
	letterSpacing: "0%",
	color: theme.palette.primary.main,
});

// Width / height layout dimensions consumed by grid-template-columns
// calculations. Color lives in `gridDividerSx` so it can follow the theme.
export const gridDividerStyle = {
	width: "16px",
	height: "110%",
} as const satisfies Pick<CSSProperties, "width" | "height">;

// Theme-aware sx for the divider Box rendered inside the grid.
export const gridDividerSx: SxProps<Theme> = (theme: Theme) => ({
	width: gridDividerStyle.width,
	height: gridDividerStyle.height,
	background: theme.palette.primary.main,
});

export const rootCardStyle = {
	display: "flex",
	flexDirection: "column",
	width: "100%",
	height: "100%",
	background: NEUTRAL[50],
	gap: 2,
} as const satisfies SxProps<Theme>;

export const headerCardStyle: SxProps<Theme> = (theme: Theme) => ({
	display: "flex",
	alignItems: "center",
	justifyContent: "flex-start",
	gap: 2,
	width: "100%",
	boxShadow: `0px 4px 4px 0px ${alpha(theme.palette.primary.main, 0.2)}`,
	p: 2,
});

/** Max locations shown per section before "Show All" is required */
export const DEFAULT_VISIBLE_LOCATIONS = 3;

export const showAllBtnClassName = "ms2-show-all-btn";
// Functional: encodes "show all" hover affordance distinct from primary actions.
const showAllLessHoverColor = "#F58427";
export const showAllBtnStyle = {
	fontSize: "15px",
	lineHeight: "15px",
	py: "1px",
	color: NEUTRAL[600],
	cursor: "pointer",
	textTransform: "none",
	transition: "color 0.2s ease, font-size 0.2s ease",
	display: "inline-flex",
	alignItems: "center",
	gap: 0.5,
	fontWeight: 500,
	"&:hover": {
		color: showAllLessHoverColor,
		fontSize: "16px",
	},
} as const satisfies SxProps<Theme>;

export const showAllFooterStyle = (isCollapsed: boolean) =>
	({
		gridColumn: `${amountOfLeadingColumns + 1} / -1`,
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
		py: 0.75,
		position: "relative",
		cursor: "pointer",
		...(!isCollapsed && {
			"&::before": {
				content: '""',
				position: "absolute",
				top: 0,
				left: "5%",
				right: "5%",
				height: "1px",
				background: `linear-gradient(to right, transparent, ${NEUTRAL[400]}, transparent)`,
			},
		}),
		"&:hover": {
			[`& .${showAllBtnClassName}`]: {
				color: showAllLessHoverColor,
			},
		},
	}) as const satisfies SxProps<Theme>;

export const sectionHoverShowAllStyle = {
	display: "contents",
} as const satisfies SxProps<Theme>;

export const contentCardStyle = {
	display: "flex",
	flexDirection: "column",
	width: "100%",
} as const satisfies SxProps<Theme>;
const gridGap = "1px" as const satisfies CSSProperties["gap"];
const leadingColumnsWidthXXL = "165px" as const satisfies CSSProperties["width"];
const leadingColumnsWidthXXLShort = "100px" as const satisfies CSSProperties["width"];
const leadingColumnsWidthXL = "150px" as const satisfies CSSProperties["width"];
const leadingColumnsWidthXLShort = "90px" as const satisfies CSSProperties["width"];
const leadingColumnsWidthXS = "130px" as const satisfies CSSProperties["width"];
const leadingColumnsWidthXSShort = "80px" as const satisfies CSSProperties["width"];
export const gridRowHeight = "26px" as const satisfies CSSProperties["height"];
export const leadingColumnsPlaceholderStyle: SxProps<Theme> = (theme: Theme) => ({
	backgroundColor: SURFACE,
	backgroundImage: `linear-gradient(${alpha(theme.palette.primary.main, 0.07)}, ${alpha(theme.palette.primary.main, 0.07)})`,
	top: 0,
	zIndex: 2,
	position: "sticky",
	gridColumn: "span 2",
});

export const getWeeksHeaderCardStyle =
	(weeksCount: number): SxProps<Theme> =>
	(theme: Theme) => ({
		gridColumn: `span ${(7 + 1) * weeksCount - 1}`, // 7 days + 1 divider per week, except for last week
		display: "grid",
		gridTemplateColumns: getCols(weeksCount),
		gap: gridGap,
		position: "sticky",
		top: 0,
		zIndex: 3,
		// Solid base prevents content bleed-through; gradient layer tints it to
		// match the page background (same 7% primary tint used in buildAppTheme).
		backgroundColor: SURFACE,
		backgroundImage: `linear-gradient(${alpha(theme.palette.primary.main, 0.07)}, ${alpha(theme.palette.primary.main, 0.07)})`,
	});

export const gridCellTypography = {
	fontSize: { xxl: "14px", xs: "12px" },
	color: INK.SECONDARY,
	lineHeight: "14px",
	fontWeight: 500,
} as const satisfies SxProps<Theme>;

export const weekHeaderCardStyle: SxProps<Theme> = (theme: Theme) => ({
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	backgroundColor: "#FFFFFF",
	backgroundImage: `linear-gradient(${alpha(theme.palette.primary.light, 0.4)}, ${alpha(theme.palette.primary.light, 0.4)})`,
	fontWeight: 600,
	fontSize: "16px",
	lineHeight: "100%",
	textTransform: "uppercase",
	color: theme.palette.primary.dark,
	gridColumn: "span 7",
	p: "2px",
});

export const gridColumnHeadersStyle: SxProps<Theme> = (theme: Theme) => ({
	display: "flex",
	alignItems: "center",
	alignContent: "center",
	justifyContent: "center",
	p: 1,
	color: theme.palette.primary.contrastText,
	background: theme.palette.primary.main,
	fontWeight: 700,
	fontSize: "14px",
	lineHeight: "100%",
	letterSpacing: "0%",
	position: "sticky",
	zIndex: 3,
	top: 24,
});

export const getSectionHeaderStyle = (weeksCount: number, isSynergyMS = false, isMS = true): SxProps<Theme> =>
	((theme: Theme) => ({
		gridColumn: `span ${2 + (isMS ? 1 : 0 /*Adding divider ratio*/) + 7 * weeksCount}`, // span 2 leading columns + N weeks
		display: isMS ? "grid" : "flex",
		gridTemplateColumns: isMS
			? {
					xxl: `${leadingColumnsWidthXXL} ${isSynergyMS ? leadingColumnsWidthXXLShort : leadingColumnsWidthXXL} ${getCols(weeksCount)}`,
					xl: `${leadingColumnsWidthXL} ${isSynergyMS ? leadingColumnsWidthXLShort : leadingColumnsWidthXL} ${getCols(weeksCount)}`,
					xs: `${leadingColumnsWidthXS} ${isSynergyMS ? leadingColumnsWidthXSShort : leadingColumnsWidthXS} ${getCols(weeksCount)}`,
				}
			: undefined,
		alignItems: "center",
		justifyContent: "space-between",
		paddingInline: "4px",
		gap: gridGap,
		color: theme.palette.primary.dark,
		backgroundColor: "#FFFFFF",
		backgroundImage: `linear-gradient(${alpha(theme.palette.primary.light, 0.4)}, ${alpha(theme.palette.primary.light, 0.4)})`,
		fontWeight: 600,
		fontStyle: "bold",
		fontSize: "14px",
		lineHeight: "100%",
		letterSpacing: "0%",
		position: "sticky",
		zIndex: 2,
		top: 42,
		maxHeight: gridRowHeight,
	})) as SxProps<Theme>;

export const gridCellStyle = {
	...gridCellTypography,
	display: "flex",
	flexDirection: "column",
	alignItems: "start",
	justifyContent: "center",
	background: SURFACE,
	paddingInline: 2,
	whiteSpace: "normal",
	overflowWrap: "anywhere",
	wordBreak: "break-word",
	textOverflow: "clip",
	width: "100%",
	minWidth: 0, // important in grid/flex context: allows shrink + wrap
	minHeight: gridRowHeight,
	height: "100%",
} as const satisfies SxProps<Theme>;

export const gridCellShiftTimesStyle = {
	paddingInline: 1,
	display: "flex",
	flexDirection: "row",
	alignItems: "center",
	justifyContent: "space-between",
	fontSize: {
		xs: "11px",
		xl: "12px",
		xxl: "14px",
	},
	fontWeight: 500,
	color: alpha(INK_BLACK, 0.4),
	background: SURFACE,
	minHeight: gridRowHeight,
	height: "100%",
} as const satisfies SxProps;

export const warningIconStyle = {
	fontSize: "14px",
	color: STATUS.ERROR.main,
	position: "absolute",
	top: 0,
	right: 0,
} as const satisfies SxProps<Theme>;

export const gridShiftCellWrapperStyle = {
	display: "flex",
	flexDirection: "column",
	alignItems: "center",
	justifyContent: "center",
	height: "100%",
	background: SURFACE,
};
export const gridShiftCellStyle = {
	...gridCellTypography,
	display: "flex",
	flexDirection: "column",
	alignItems: "center",
	justifyContent: "center",
	background: SURFACE,
	width: "100%",
	gap: gridGap,
	minWidth: 0,
	position: "relative",
	height: "fit-content",
	minHeight: gridRowHeight,
	p: "1px",
} as const satisfies SxProps<Theme>;

export const gridShiftCellInnerStyle = {
	...gridCellTypography,
	whiteSpace: "nowrap",
	overflow: "hidden",
	textOverflow: "ellipsis",
	width: "100%",
};

export const getShiftCellStyle = (_: boolean, strikethrough: boolean) =>
	({
		...gridCellTypography,
		width: "100%",
		cursor: "pointer",
		minHeight: gridRowHeight,
		":hover": {
			background: "var(--inhouse-primary-alpha-08)",
		},
		px: 1,
		py: 0.25,
		display: "flex",
		alignItems: "center",
		border: `1px solid ${alpha(NEUTRAL[400], 0.5)}`,
		textDecoration: strikethrough ? "line-through" : "none",
	}) as const satisfies SxProps<Theme>;

export const emptyGridShiftCellStyle = {
	...gridShiftCellStyle,
	background: "rgba(207, 216, 220, 0.5)",

	justifyContent: "center",
	alignItems: "center",
	// Make the entire cell a click target so users don't have to land on the
	// 18px-tall + button inside it.
	"& button": {
		height: "100%",
		width: "100%",
	},
} as const satisfies SxProps<Theme>;

export const addButtonStyle = {
	...gridCellTypography,
	background: "transparent",
	justifyContent: "center",
	alignItems: "center",
	height: gridRowHeight,
	width: "100%",
	p: 0,
	m: 0,
	minHeight: 0,
} as const satisfies SxProps<Theme>;

export const addButtonLeadingIconGap = "4px" as const satisfies CSSProperties["gap"];

/**
 * Location and Shift columns
 */
export const amountOfLeadingColumns = 2;
export type RecurringShiftInCell = {
	recurringShift: RecurringShift;
	isFutureShift: boolean;
	isOptimisticallyAdded: boolean;
	longPtoForShift?: {
		ptoStartDate: DateTime;
		ptoEndDate?: DateTime;
	};
	isCredentialed: boolean;
};
export type ScheduleDayAtLocationData = {
	dayNumber: number;
	recurringShiftsInCell: RecurringShiftInCell[];
	dayDate: DateTime;
};
export type ScheduleWeekData = {
	weekNumber: number;
	days: ScheduleDayAtLocationData[];
};
export type ScheduleData = {
	location: DepartmentLocation;
	weeklyShifts: ScheduleWeekData[];
	isLocationOutsideOfConfig?: boolean;
	shiftType: ShiftType;
};
export type Section = string;

/**
 * Weekday indices (0=Sun … 6=Sat) the grid actually placed a shift on for a location — the single
 * source of truth for "which days are occupied", shared by the cells and the delete guard. A day
 * counts only when an occurrence was materialized (cadence + end_date via `buildScheduleData`), so an
 * expired recurring row that renders nothing never blocks deleting the location.
 */
export const getOccupiedDays = (weeklyShifts: ScheduleWeekData[]): number[] =>
	Array.from(
		new Set(
			weeklyShifts.flatMap((week) =>
				week.days.filter((day) => day.recurringShiftsInCell.length > 0).map((day) => getDayIndex(day.dayDate.setLocale("en").toFormat("cccc"))),
			),
		),
	).filter((idx): idx is number => idx !== null);
export type SectionLocationMap = Partial<Record<Section, ScheduleData[]>>;
export type SectionLocationMapReadOnly = Readonly<Partial<Record<Section, Readonly<ScheduleData[]>>>>;

const getCols = (weeksCount: number) =>
	`${Array.from({ length: weeksCount - 1 }, () => `repeat(7, minmax(0, 1fr)) ${gridDividerStyle.width}`).join(" ")} repeat(7, minmax(0, 1fr))`;

const getBaseGridSx = (amountOfWeeksInGrid: number, isSynergyMS: boolean, isMS = true): SxProps<Theme> => {
	const dayCols = isMS ? getCols(amountOfWeeksInGrid) : `repeat(${7 * amountOfWeeksInGrid}, minmax(0, 1fr))`;
	return {
		width: "100%",
		display: "grid",
		gap: gridGap,

		// two leading fixed columns
		gridTemplateColumns: {
			xxl: `${leadingColumnsWidthXXL} ${isSynergyMS ? leadingColumnsWidthXXLShort : leadingColumnsWidthXXL} ${dayCols}`,
			xl: `${leadingColumnsWidthXL} ${isSynergyMS ? leadingColumnsWidthXLShort : leadingColumnsWidthXL} ${dayCols}`,
			xs: `${leadingColumnsWidthXS} ${isSynergyMS ? leadingColumnsWidthXSShort : leadingColumnsWidthXS} ${dayCols}`,
		},
		justifyContent: "start",
		boxSizing: "border-box",
		position: "relative",
	} as const satisfies SxProps<Theme>;
};

export const getGridSx = (amountOfWeeksInGrid: number, offsetHeight: number, isSynergyMS: boolean, isMS = true): SxProps<Theme> => {
	return {
		...getBaseGridSx(amountOfWeeksInGrid, isSynergyMS, isMS),
		gridTemplateRows: `${gridRowHeight} ${gridRowHeight}`,
		gridAutoRows: "auto",
		maxHeight: `calc(100% - ${offsetHeight}px)`,
		height: "fit-content",
		overflowY: "auto",
		overflowX: "hidden",
	} as const satisfies SxProps<Theme>;
};

export const getRowGridSx = (amountOfWeeksInGrid: number, isDragging: boolean, isSynergyMS: boolean, isMS = true): SxProps<Theme> => {
	return {
		...getBaseGridSx(amountOfWeeksInGrid, isSynergyMS, isMS),
		height: "fit-content",
		maxHeight: "fit-content",
		gridColumn: `1 / span ${amountOfLeadingColumns + (isMS ? 1 : 0 /*divider ration*/) + 7 * amountOfWeeksInGrid}`,
		gridRow: "span 1",
		zIndex: 1,
		...(!isDragging
			? {
					":hover": {
						boxShadow: "0px 0px 3px 2px var(--inhouse-primary-light)",
						zIndex: 3,
						transition: "all 150ms",
						background: "var(--inhouse-primary-alpha-08)",
					},
					":hover > *:nth-of-type(1), :hover > *:nth-of-type(2)": {
						backgroundColor: "var(--inhouse-primary-light)",
						color: "var(--inhouse-primary-contrast)",
						fontWeight: 600,
						transition: "all 150ms",
					},
				}
			: {}),
	} as const satisfies SxProps<Theme>;
};

export const masterScheduleSortingOptions: Array<MultiSortOption<DepartmentLocation>> = [
	{
		sortColumns: ["shift_start", "shift_end"],
		label: "Shift Times",
	},
	{
		sortColumns: ["location"],
		label: "Location Name",
	},
];

export const masterScheduleDefaultSorting: Array<SortingState<DepartmentLocation>> = [
	{
		sortColumns: ["location"],
		label: "Location Name",
		sortOrder: "asc",
	},
	{
		sortColumns: ["shift_start", "shift_end"],
		label: "Shift Times",
		sortOrder: "asc",
	},
];

/**
 * Highlights all shift cells of a nurse when hovering over one of them using CSS class toggling.
 * @param nurseId
 * @param on - Whether to add or remove the highlight class
 * @param rootId - Root element to scope the query to. Defaults to the V2 root; the Consecutive view
 *   passes its own root id since it reuses the same `ClickableShiftCell`/highlight class.
 */
export const toggleMasterScheduleNurseCellHighlight = (nurseId: Nurse["id"], on: boolean, rootId: string = MASTER_SCHEDULE_ROOT_ID): void => {
	const root = document.getElementById(rootId);
	if (!root) return;

	// Don't highlight when dragging a section
	const isDraggingSection = root.getAttribute("data-dragging-section") === "true";
	if (isDraggingSection) return;

	const nodes = root.querySelectorAll(`[data-nurse-id="${nurseId}"] .ms2-shift-cell-inner`);
	nodes.forEach((node) => {
		if (on) node.classList.add("ms2-highlight");
		else node.classList.remove("ms2-highlight");
	});
};

/**
 * Filters recurring shifts based on given criteria
 * @param recurringShiftsData - list of recurring shifts
 * @param recurringTypes - when provided, only include shifts matching these recurrence types
 * @param interval - whether to include only shifts with interval less than or equal to master schedule weeks
 * @param masterScheduleWeeks - number of weeks in the master schedule
 * @returns filtered list of recurring shifts
 */
export const filterRecurringShifts = ({
	recurringShiftsData,
	recurringTypes,
	interval,
	masterScheduleWeeks,
}: {
	recurringTypes?: (keyof typeof RecurringShiftRecurrenceType)[];
	interval?: boolean;
	recurringShiftsData: RecurringShift[] | undefined;
	masterScheduleWeeks: number;
}) => {
	return recurringShiftsData?.filter((shift) => {
		return (
			!shift.deleted &&
			(!recurringTypes || recurringTypes.includes(shift.recurringParams.recurringType)) &&
			(!interval || shift.recurringParams.interval <= masterScheduleWeeks)
		);
	});
};

/**
 * Schedule data building
 *
 * `buildScheduleData` is a PURE function: it derives the full master-schedule grid
 * (`SectionLocationMap`) from the server recurring shifts, the department config, the active
 * filters/sort, and an optional optimistic overlay. It performs NO side effects — analytics for
 * shifts that can't be placed are returned as `placementErrors` so the caller can log them in an
 * effect. Keeping it pure lets the caller derive `calculatedData` with a `useMemo` instead of an
 * effect + `setState`.
 */

/** A recurring shift fed to the builder. `isOptimistic` marks a not-yet-confirmed drag/drop copy. */
export type ScheduleBuilderShift = { shift: RecurringShift; isOptimistic: boolean };

export type SchedulePlacementError = { section: string; recurringShiftId: number | undefined; location: string | undefined };

export type BuildScheduleDataParams = {
	/** Server shifts (isOptimistic:false) first, then optimistic overlay entries (isOptimistic:true). */
	shifts: ScheduleBuilderShift[] | undefined;
	chosenDepartment: Department;
	weeksAndActiveState: WeeksAndActiveState[];
	amountOfWeeks: number;
	weekStartDay: number;
	timezone: string;
	isSynergyMS: boolean;
	jobManagementEnabled: boolean;
	selectedRoleFilters: (NurseRole | "all")[];
	selectedDepartmentTypes: (DepartmentType | "all")[];
	selectedJobFilters: string[];
	selectedLocationFilters: string[];
	selectedShiftFilter: ShiftFilterType | ExactShiftFilterType;
	/** When set, only sections containing this nurse's shifts are kept. */
	nurseFilterId: number | null;
	masterScheduleSortState: SortingState<DepartmentLocation>[];
	sectionOrder: Section[];
	customStringOrderMap: Map<string, number> | null;
	locationIdsAndCredentials: Map<string, number[]>;
	getNurseById: (id: number) => Nurse | undefined;
	getNurseFormattedName: (nurseId: number) => string;
	longPtoMinimumDays: number;
	noLocationString: string;
};

export type BuildScheduleDataResult = {
	data: SectionLocationMap;
	placementErrors: SchedulePlacementError[];
};

/**
 * Composite key identifying a single placed shift occurrence, used to suppress an optimistic
 * overlay copy once the saved server shift lands. Times are normalized to HH:mm and the key is
 * per-weekday (the backend may fold the dragged weekday into an existing recurrence, so callers
 * match by weekday membership rather than full weekday-array equality).
 */
const getOptimisticShiftMatchKey = ({
	nurseId,
	location,
	shiftStart,
	shiftEnd,
	startDate,
	weekday,
}: {
	nurseId: number;
	location: string | undefined;
	shiftStart: string;
	shiftEnd: string;
	startDate: string;
	weekday: string;
}): string => [nurseId, location ?? "", shiftStart.substring(0, 5), shiftEnd.substring(0, 5), startDate, weekday.toLowerCase()].join("|");

/**
 * Final pass over a built `SectionLocationMap`, shared by the weekly (`buildScheduleData`) and
 * consecutive builders: (1) sort each section's locations, (2) order sections (custom order first,
 * then alphabetical), and (3) when a nurse filter is active, keep only sections that contain that
 * nurse's shifts. Pure — returns a new ordered map.
 */
export const finalizeSectionMap = ({
	data,
	masterScheduleSortState,
	customStringOrderMap,
	sectionOrder,
	nurseFilterId,
}: {
	data: SectionLocationMap;
	masterScheduleSortState: SortingState<DepartmentLocation>[];
	customStringOrderMap: Map<string, number> | null;
	sectionOrder: Section[];
	nurseFilterId: number | null;
}): SectionLocationMap => {
	for (const key of Object.keys(data)) {
		data[key as Section] = data[key as Section]?.sort((a, b) => multiSortComparator(masterScheduleSortState, customStringOrderMap)(a.location, b.location));
	}

	const sortedKeys = Object.keys(data).sort((a, b) => {
		const aIndex = sectionOrder.indexOf(a);
		const bIndex = sectionOrder.indexOf(b);
		if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
		if (aIndex !== -1) return -1;
		if (bIndex !== -1) return 1;
		return a.localeCompare(b);
	});

	return sortedKeys.reduce<SectionLocationMap>((acc, key) => {
		if (nurseFilterId == null) {
			acc[key as Section] = data[key as Section];
		} else if (data[key as Section] != null) {
			acc[key as Section] = data[key as Section]?.filter((value) =>
				value.weeklyShifts.some((week) =>
					week.days.some((day) => day.recurringShiftsInCell.some((shiftInCell) => shiftInCell.recurringShift.nurseId === nurseFilterId)),
				),
			);
		}
		return acc;
	}, {});
};

export const buildScheduleData = (params: BuildScheduleDataParams): BuildScheduleDataResult => {
	const {
		shifts,
		chosenDepartment,
		weeksAndActiveState,
		amountOfWeeks,
		weekStartDay,
		timezone,
		isSynergyMS,
		jobManagementEnabled,
		selectedRoleFilters,
		selectedDepartmentTypes,
		selectedJobFilters,
		selectedLocationFilters,
		selectedShiftFilter,
		nurseFilterId,
		masterScheduleSortState,
		sectionOrder,
		customStringOrderMap,
		locationIdsAndCredentials,
		getNurseById,
		getNurseFormattedName,
		longPtoMinimumDays,
		noLocationString,
	} = params;

	const data: SectionLocationMap = {};
	const placementErrors: SchedulePlacementError[] = [];

	// Keys of the confirmed (server) shift occurrences, used to suppress duplicate optimistic copies.
	const serverShiftKeys = new Set<string>();
	for (const { shift, isOptimistic } of shifts ?? []) {
		if (isOptimistic || shift.recurringParams.recurringType !== "Weekly") continue;
		for (const weekday of shift.recurringParams.weekdays) {
			serverShiftKeys.add(
				getOptimisticShiftMatchKey({
					nurseId: shift.nurseId,
					location: shift.location,
					shiftStart: shift.shiftStart,
					shiftEnd: shift.shiftEnd,
					startDate: shift.startDate,
					weekday,
				}),
			);
		}
	}

	// 1. Scaffold empty location rows for every configured location that passes the filters.
	const allLocationSections = new Set([
		...chosenDepartment.locations.map((value) => value.section),
		"" /** Ensure an empty section is always created — avoids edge-case bugs where we miss shifts. */,
	]);
	for (const locationSection of allLocationSections) {
		if (!data[locationSection]) {
			data[locationSection] = [];
		}
		for (const departmentLocationConfig of chosenDepartment.locations.filter((departmentLocation) => departmentLocation.section === locationSection)) {
			const passesRoleOrTypeFilter = isSynergyMS
				? selectedDepartmentTypes.includes("all") ||
					(departmentLocationConfig.departmentType && selectedDepartmentTypes.includes(departmentLocationConfig.departmentType))
				: jobManagementEnabled
					? !departmentLocationConfig.jobId || selectedJobFilters.includes(departmentLocationConfig.jobId)
					: departmentLocationConfig.roles.some((role) => role === "all" || selectedRoleFilters.includes(NurseRole[NurseFullRoleShort[role]]));
			const passesLocationFilter = selectedLocationFilters.length === 0 || selectedLocationFilters.includes(departmentLocationConfig.location ?? "");
			if (!(passesRoleOrTypeFilter && passesLocationFilter)) continue;

			const startTime = DateTime.fromFormat(departmentLocationConfig.shift_start.substring(0, 5), "HH:mm");
			const endTime = DateTime.fromFormat(departmentLocationConfig.shift_end.substring(0, 5), "HH:mm");
			if (!isSelectedTimeFrameValidForShiftFilter({ startTime, endTime, selectedShiftFilter, dayStart: chosenDepartment.dayStart })) continue;

			const newlyScheduledData: ScheduleData = {
				location: {
					...departmentLocationConfig,
					// handles edge case of mismatching format which affects the sorting order by shift times.
					shift_start: departmentLocationConfig.shift_start.substring(0, 5),
					shift_end: departmentLocationConfig.shift_end.substring(0, 5),
				},
				weeklyShifts: [],
				shiftType: chosenDepartment.shift_types[getShiftIndexByShiftTimes({ startTime, endTime })],
			};
			for (let weekIdx = 0; weekIdx < amountOfWeeks; weekIdx++) {
				const scheduleWeekData: ScheduleWeekData = { days: [], weekNumber: weekIdx + 1 };
				for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
					scheduleWeekData.days.push({
						recurringShiftsInCell: [],
						dayNumber: dayIndex + 1,
						dayDate: weeksAndActiveState[weekIdx].firstDate.plus({ days: dayIndex }),
					});
				}
				newlyScheduledData.weeklyShifts.push(scheduleWeekData);
			}
			data[locationSection].push(newlyScheduledData);
		}
	}

	// 2. Place each shift (server then optimistic) into the scaffolded grid.
	const placeShift = (recurringShiftsData: RecurringShift, isOptimistic: boolean): void => {
		const recurrenceStart = DateTime.fromFormat(recurringShiftsData.startDate, "yyyy-MM-dd", { zone: timezone });
		const recurrenceEnd = recurringShiftsData.endDate ? DateTime.fromFormat(recurringShiftsData.endDate, "yyyy-MM-dd", { zone: timezone }) : null;
		const recurrenceStartTime = DateTime.fromFormat(recurringShiftsData.shiftStart.substring(0, 5), "HH:mm", { zone: timezone });
		const _recurrenceEndTime = DateTime.fromFormat(recurringShiftsData.shiftEnd.substring(0, 5), "HH:mm", { zone: timezone });
		const recurrenceEndTime = _recurrenceEndTime.hour <= recurrenceStartTime.hour ? _recurrenceEndTime.plus({ day: 1 }) : _recurrenceEndTime;

		if (
			!isSelectedTimeFrameValidForShiftFilter({
				startTime: recurrenceStartTime,
				endTime: recurrenceEndTime,
				selectedShiftFilter,
				dayStart: chosenDepartment.dayStart,
			})
		) {
			return;
		}

		const relevantWeekIndexes: number[] = [];
		/** Weeks for which recurrence start is after the week end date — still shown, but as future shifts. */
		const weekIndexesShiftIsPlannedAheadOf = weeksAndActiveState
			.map((week, index) => (recurrenceStart > week.firstDate.plus({ days: 6 }) ? index : null))
			.filter((index) => index !== null);

		for (const currentWeek of weeksAndActiveState) {
			const doesRecurrenceStartFallInFutureWeeks = weekIndexesShiftIsPlannedAheadOf.includes(currentWeek.weekIndex);
			const shouldMatchWithStartOfWeek = recurrenceStart <= currentWeek.firstDate;
			const shouldMatchWithEndOfWeek = doesRecurrenceStartFallInFutureWeeks && recurrenceStart >= currentWeek.firstDate.plus({ days: 6 });
			const dateToCheckAgainst = doesRecurrenceStartFallInFutureWeeks
				? shouldMatchWithEndOfWeek
					? currentWeek.firstDate.plus({ days: 6 })
					: currentWeek.firstDate
				: shouldMatchWithStartOfWeek
					? currentWeek.firstDate
					: currentWeek.firstDate.plus({ days: 6 });
			const weeksPassedFloat = dateToCheckAgainst.diff(recurrenceStart, "days").days / 7;
			const weeksPassed = doesRecurrenceStartFallInFutureWeeks
				? shouldMatchWithEndOfWeek
					? Math.floor(weeksPassedFloat)
					: Math.ceil(weeksPassedFloat)
				: shouldMatchWithStartOfWeek
					? Math.ceil(weeksPassedFloat)
					: Math.floor(weeksPassedFloat);
			if (weeksPassed % recurringShiftsData.recurringParams.interval === 0) {
				relevantWeekIndexes.push(currentWeek.weekIndex);
			}
		}
		if (relevantWeekIndexes.length === 0) return;

		const relevantDayIndexes: number[] = [];
		if (recurringShiftsData.recurringParams.recurringType === "Weekly") {
			relevantDayIndexes.push(
				...recurringShiftsData.recurringParams.weekdays
					.map((day) => {
						const sunAnchored = getDayIndex(day);
						return sunAnchored === null ? null : (sunAnchored - weekStartDay + 7) % 7;
					})
					.filter((dayIndex) => dayIndex != null),
			);
		}
		if (relevantDayIndexes.length === 0) return;

		const foundNurse = getNurseById(recurringShiftsData.nurseId);
		if (!foundNurse) return;

		const relevantRole = NurseRole[NurseFullRoleShort[foundNurse.role]];
		const relevantLocations = Array.from(
			new Set(
				chosenDepartment.locations.filter(
					(departmentLocation) =>
						locationMatchesShiftName(departmentLocation, recurringShiftsData) &&
						(isSynergyMS || locationMatchesShiftRoleOrJob(departmentLocation, recurringShiftsData, relevantRole, jobManagementEnabled)),
				),
			),
		);
		const relevantLocationsByTime = isSynergyMS
			? relevantLocations
			: relevantLocations.filter((departmentLocation) => locationMatchesShiftTimes(departmentLocation, recurringShiftsData));

		/** Find the most time-fitting (or abc-fitting) section; fall back to an empty location string. */
		const relevantSection =
			(relevantLocationsByTime.length > 0 ? relevantLocationsByTime : relevantLocations)
				.map((loc) => loc.section)
				.sort()
				.at(0) ?? "";
		/** If the section couldn't be determined by location+time+roles, try matching location+time in the config. */
		const sectionIfEmptySectionNotExists =
			chosenDepartment.locations
				.filter(
					(loc) =>
						loc.location === recurringShiftsData.location &&
						loc.shift_start.substring(0, 5) === recurringShiftsData.shiftStart.substring(0, 5) &&
						loc.shift_end.substring(0, 5) === recurringShiftsData.shiftEnd.substring(0, 5),
				)
				.at(0)?.section ?? "";

		const discreetSection = relevantSection || sectionIfEmptySectionNotExists;
		const currentData = data[discreetSection];
		if (!currentData) {
			placementErrors.push({ section: discreetSection, recurringShiftId: recurringShiftsData.id, location: recurringShiftsData.location });
			return;
		}

		const locationOfRecurringShift = recurringShiftsData.location ?? "" /* In case the recurring shift is not linked to a location. */;
		let locationData = currentData.find((dataEntry) => {
			if (isSynergyMS) {
				// Simplified matching for Synergy MS — just match by location name.
				return dataEntry.location.location === locationOfRecurringShift;
			}
			return (
				locationMatchesShiftName(dataEntry.location, recurringShiftsData) &&
				locationMatchesShiftTimes(dataEntry.location, recurringShiftsData) &&
				locationMatchesShiftRoleOrJob(dataEntry.location, recurringShiftsData, relevantRole, jobManagementEnabled) &&
				recurringShiftsData.recurringParams.recurringType === "Weekly" &&
				locationMatchesShiftWeekdays(dataEntry.location, recurringShiftsData.recurringParams.weekdays)
			);
		});
		let shouldAddLocationData = false;

		if (!locationData) {
			// No location with the exact start/end time exists — create a temp "outside of config" one.
			const newLocationData: Partial<DepartmentLocation> = {
				location: recurringShiftsData.location ?? "",
				shift_start: recurringShiftsData.shiftStart,
				shift_end: recurringShiftsData.shiftEnd,
				roles: [NurseFullRole[relevantRole]],
				department_id: chosenDepartment.department_id,
				section: discreetSection,
				days: ["sun", "mon", "tue", "wed", "thu", "fri", "sat"],
				...(jobManagementEnabled && recurringShiftsData.jobId ? { jobId: recurringShiftsData.jobId } : {}),
			};
			locationData = {
				location: newLocationData as DepartmentLocation,
				isLocationOutsideOfConfig: true,
				weeklyShifts: Array.from({ length: amountOfWeeks }, (_, weekIndex) => ({
					weekNumber: weekIndex + 1,
					days: Array.from({ length: 7 }, (_, dayIndex) => ({
						dayNumber: dayIndex + 1,
						recurringShiftsInCell: [],
						dayDate: weeksAndActiveState[weekIndex].firstDate.plus({ days: dayIndex }),
					})),
				})),
				shiftType:
					chosenDepartment.shift_types[
						getShiftIndexByShiftTimes({
							startTime: DateTime.fromFormat((newLocationData.shift_start ?? "").substring(0, 5), "HH:mm"),
							endTime: DateTime.fromFormat((newLocationData.shift_end ?? "").substring(0, 5), "HH:mm"),
						})
					],
			};
			shouldAddLocationData = true;
		}

		const passesRoleOrTypeFilter = isSynergyMS
			? selectedDepartmentTypes.includes("all") ||
				(locationData.location.departmentType && selectedDepartmentTypes.includes(locationData.location.departmentType))
			: jobManagementEnabled
				? !locationData.location.jobId || selectedJobFilters.includes(locationData.location.jobId)
				: locationData.location.roles.includes("all") ||
					locationData.location.roles
						.filter((r) => r !== "all")
						.some((role) => selectedRoleFilters.includes(NurseRole[NurseFullRoleShort[role as keyof typeof NurseFullRoleShort]]));
		const passesLocationFilter = selectedLocationFilters.length === 0 || selectedLocationFilters.includes(locationData.location.location ?? "");
		if (!(passesRoleOrTypeFilter && passesLocationFilter)) return;

		let placedOccurrences = 0;
		relevantWeekIndexes.forEach((weekIndex) => {
			const weeklyShifts = locationData.weeklyShifts[weekIndex];
			if (!weeklyShifts) return;
			relevantDayIndexes.forEach((dayIndex) => {
				const currentDate = weeksAndActiveState[weekIndex].firstDate.plus({ days: dayIndex });
				if (recurrenceEnd && currentDate > recurrenceEnd) return;
				const dayData: ScheduleDayAtLocationData = weeklyShifts.days[dayIndex];
				if (!dayData) return;

				// Suppress an optimistic copy once a confirmed server shift occupies the same occurrence.
				if (isOptimistic) {
					const weekday = currentDate.setLocale("en").toFormat("cccc");
					const matchKey = getOptimisticShiftMatchKey({
						nurseId: recurringShiftsData.nurseId,
						location: recurringShiftsData.location,
						shiftStart: recurringShiftsData.shiftStart,
						shiftEnd: recurringShiftsData.shiftEnd,
						startDate: recurringShiftsData.startDate,
						weekday,
					});
					if (serverShiftKeys.has(matchKey)) return;
				}

				const longPtoForShift = nursePreferencesToDaysOffAndPto(foundNurse).find((dayOff) =>
					dayOff.dateFrom && dayOff.dateTo
						? dayOff.dateTo.diff(dayOff.dateFrom, "days").days >= longPtoMinimumDays &&
							currentDate >= dayOff.dateFrom.startOf("day") &&
							currentDate <= dayOff.dateTo.endOf("day")
						: dayOff.dateFrom
							? currentDate > dayOff.dateFrom.startOf("day")
							: false,
				);
				/** Credentialing only matters for Synergy MS; assume credentialed when no/empty location. */
				const isCredentialed =
					isSynergyMS && locationData?.location.location && locationData?.location.location !== noLocationString
						? (locationIdsAndCredentials.get(locationData.location.id)?.includes(recurringShiftsData.nurseId) ?? false)
						: true;

				dayData.recurringShiftsInCell.push({
					recurringShift: recurringShiftsData,
					isFutureShift: weekIndexesShiftIsPlannedAheadOf.includes(weekIndex),
					isOptimisticallyAdded: isOptimistic,
					longPtoForShift: longPtoForShift ? { ptoStartDate: longPtoForShift.dateFrom, ptoEndDate: longPtoForShift.dateTo } : undefined,
					isCredentialed,
				});
				placedOccurrences++;
				dayData.recurringShiftsInCell.sort((a, b) =>
					getNurseFormattedName(a.recurringShift.nurseId).localeCompare(getNurseFormattedName(b.recurringShift.nurseId)),
				);
			});
		});

		// Only surface a synthetic "outside of config" row when the shift actually placed an occurrence in
		// view. A spent recurring row (renders nothing) must not resurrect a ghost location — the same
		// occupancy rule the delete guard uses (getOccupiedDays).
		if (shouldAddLocationData && placedOccurrences > 0) {
			currentData.push(locationData);
		}
	};

	for (const { shift, isOptimistic } of shifts ?? []) {
		placeShift(shift, isOptimistic);
	}

	// 3 & 4. Sort locations, order sections, and apply the nurse filter.
	const ordered = finalizeSectionMap({ data, masterScheduleSortState, customStringOrderMap, sectionOrder, nurseFilterId });

	return { data: ordered, placementErrors };
};
